import { useEffect, useContext, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { format, getYear } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useCrud } from '../../../lib/hooks';
import {
    AppContext,
    SttButtonCancel,
    SttButtonDelete,
    SttButtonSave,
    SttFormDate,
    SttFormSelect,
    SttFormText,
    SttLabelForm,
    SttSelectFacility,
    SttSelectUser,
    SttBookingScheduleSlotList,
    SttDialog,
} from '../../../sporttia/all';
import { getPath } from '../../Pages';
import BookingScheduleSlotDialog from './BookingScheduleSlotDialog';
import BookingScheduleDeleteDialog from './BookingScheduleDeleteDialog';
import DateBuilder from '../../../lib/DateBuilder';
import { PERMISSIONS } from '../../../lib/permissions';

type BookingSchedule = {
    id?: string;
    name?: string;
    dateini?: string;
    dateend?: string;
    status?: string;
    trash?: number;
    created?: string;
    user?: {
        id?: string;
        fullName?: string;
    };
    facility?: {
        id?: number;
    };
};

type BookingScheduleSlot = {
    id?: string;
    weekdays?: number[];
    timeini?: string;
    timeend?: string;
    count?: number;
};

type Props = {
    id?: string;
    trash?: unknown;
    onSaveSlot?: () => void;
    onSaveBookingSchedule?: (bookingSchedule: BookingSchedule) => void;
    bookingScheduleInit?: BookingSchedule;
};

type LocalUser = {
    id: number;
    name?: string;
    surname1?: string;
    surname2?: string;
    login?: string;
    fullName?: string;
};

type LocalMship = {
    id: number;
    fullName: string;
    user: LocalUser;
};

/**
 * BookingSchedule : Alquiler fijo
 * Object match : navigation params match
 */
export default function BookingScheduleDetails({
    id,
    onSaveSlot,
    onSaveBookingSchedule,
    bookingScheduleInit,
}: Props): React.ReactElement | false {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [
        bookingSchedule,
        setBookingSchedule,
        setProperty,
        Get,
        Post,
        Put,
        Delete,
    ] = useCrud<BookingSchedule>(bookingScheduleInit);
    const [slots, setSlots] = useState<BookingScheduleSlot[]>([]);
    const [viewSlot, setViewSlot] = useState<BookingScheduleSlot | null>(null);
    const [savingSlots, setSavingSlots] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [openDeleteResponseDialog, setOpenDeleteResponseDialog] =
        useState<boolean>(false);
    const [showCreatedInfo, setShowCreatedInfo] = useState<boolean>(false);

    // Load schedule slots
    function loadSlots() {
        Get<{ rows?: BookingScheduleSlot[] }>(
            `/bookings/schedules/${id}/slots`,
        ).then((response) => {
            if (response.rows) {
                setSlots(response.rows);
            }
        });
    }

    useEffect(() => {
        if (bookingScheduleInit) {
            setBookingSchedule(bookingScheduleInit);
        }
    }, [bookingScheduleInit?.id]);

    // Initialize (load by id or create)
    useEffect(() => {
        if (id === 'create') {
            setBookingSchedule({
                name: '',
                dateini: format(new Date(), 'yyyy-MM-dd'),
                dateend: `${getYear(new Date())}-12-31`,
                status: 'ACTIVE',
            });
            cxt?.setHeader({
                title: t('newBookingSchedule'),
            });
        } else {
            loadSlots();
        }
    }, [id]);

    // Save. Put if 'id' is defined, POST otherwise.
    function saveBookingSchedule() {
        // Validate name is required when creating
        if (!bookingSchedule?.id && !bookingSchedule?.name?.trim()) {
            cxt?.showMessage('E', t('validation.nameRequired'));
            return;
        }

        setLoading(true);
        if (bookingSchedule?.id) {
            Put<{ bookingSchedule?: BookingSchedule }>(
                `/bookings/schedules/${bookingSchedule.id}`,
                {
                    status: bookingSchedule?.status,
                    name: bookingSchedule?.name,
                },
            )
                .then((response) => {
                    if (response.bookingSchedule && onSaveBookingSchedule) {
                        onSaveBookingSchedule(response.bookingSchedule);
                    }
                })
                .finally(() => setLoading(false));
        } else {
            const params = {
                name: bookingSchedule?.name,
                idUser: bookingSchedule?.user?.id,
                idFacility: bookingSchedule?.facility?.id,
                dateini: bookingSchedule?.dateini,
                dateend: bookingSchedule?.dateend,
                status: bookingSchedule?.status || 'ACTIVE',
            };

            Post<{ bookingSchedule?: BookingSchedule }>(
                `/scs/${cxt?.sc?.id}/bookings/schedules`,
                params,
            )
                .then((response) => {
                    if (response.bookingSchedule && onSaveBookingSchedule) {
                        onSaveBookingSchedule(response.bookingSchedule);
                    }
                })
                .finally(() => setLoading(false));
        }
    }

    // Delete with API
    function deleteBookingSchedule(userResponse: boolean) {
        Delete(`/bookings/schedules/${bookingSchedule?.id}`, {
            deleteAllBookings: userResponse,
        }).then(() => {
            navigate(getPath('bookingSchedules', { replace: true }));
        });
    }

    if (!bookingSchedule) {
        return false;
    }

    // Save Slot: POST only since these can't be edited
    function saveSlot(slot: BookingScheduleSlot) {
        if (slot.id) {
            return;
        }

        setSavingSlots(true);

        cxt?.api<{ bookingScheduleSlot?: BookingScheduleSlot }>(
            'POST',
            `/bookings/schedules/${bookingSchedule.id}/slots`,
            {
                params: {
                    ...slot,
                },
                success: (response) => {
                    if (response.bookingScheduleSlot) {
                        setSlots([...slots, response.bookingScheduleSlot]);
                        setShowCreatedInfo(true);
                        if (onSaveSlot) onSaveSlot();
                    }

                    if (
                        response.bookingScheduleSlot &&
                        response.bookingScheduleSlot.count &&
                        response.bookingScheduleSlot.count > 0
                    ) {
                        cxt?.showMessage('S', t(`Created`));
                    }

                    setViewSlot(null);

                    setSavingSlots(false);
                },
                error: (response: unknown) => {
                    if (
                        response &&
                        typeof response === 'object' &&
                        'error' in response
                    ) {
                        const errorResponse = response as {
                            error?: { msg?: string };
                        };
                        if (errorResponse.error?.msg) {
                            cxt?.showMessage('E', errorResponse.error.msg);
                        }
                    }
                    setSavingSlots(false);
                },
            },
        );
    }

    // Delete slot
    function deleteSlot(slot: BookingScheduleSlot) {
        if (!slot.id) {
            return;
        }

        Delete<{ bookingScheduleSlot?: BookingScheduleSlot }>(
            `/bookings/schedules/slots/${slot.id}`,
        ).then((response) => {
            if (response.bookingScheduleSlot) {
                setSlots(slots.filter((slt) => slt.id !== slot.id));
            }
            setViewSlot(null);
        });
    }

    return (
        <>
            <Grid container spacing={3}>
                {bookingSchedule.id && (
                    <Grid size={{ xs: 12 }}>
                        <SttLabelForm
                            title={t('Created')}
                            text={new DateBuilder(
                                bookingSchedule.created,
                                cxt?.sc?.timezone?.name,
                                cxt?.lang?.key,
                            ).dmy()}
                        />
                    </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                    <SttFormText
                        name="name"
                        caption={t('Name')}
                        defVal={bookingSchedule.name}
                        onChange={({ name, value }) =>
                            setProperty({
                                name: name as keyof BookingSchedule,
                                value: value as string,
                            })
                        }
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <SttFormSelect
                        name="status"
                        caption={t('Status')}
                        defVal={bookingSchedule.status}
                        options={cxt?.constants?.scheduleStatuses}
                        onChange={({ name, value }) =>
                            setProperty({
                                name: name as keyof BookingSchedule,
                                value: value as string,
                            })
                        }
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <SttFormDate
                        disabled={!!bookingSchedule.id}
                        caption={t('dateIni')}
                        name="dateini"
                        defVal={bookingSchedule.dateini}
                        onChange={({ name, value }) =>
                            setProperty({
                                name: name as keyof BookingSchedule,
                                value: value as string,
                            })
                        }
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <SttFormDate
                        disabled={!!bookingSchedule.id}
                        caption={t('dateEnd')}
                        name="dateend"
                        defVal={bookingSchedule.dateend}
                        onChange={({ name, value }) =>
                            setProperty({
                                name: name as keyof BookingSchedule,
                                value: value as string,
                            })
                        }
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <SttSelectUser
                        controlDisabled={!!bookingSchedule.id}
                        caption={t('User')}
                        name="user"
                        user={
                            (bookingSchedule.user
                                ? {
                                      id: Number(bookingSchedule.user.id) || 0,
                                      fullName: bookingSchedule.user.fullName,
                                      name: bookingSchedule.user.fullName,
                                  }
                                : undefined) as any
                        }
                        onSelect={(result) => {
                            if (result && 'user' in result) {
                                // It's a LocalMship
                                const mshipObj =
                                    result as unknown as LocalMship;
                                setProperty({
                                    name: 'user',
                                    value: {
                                        fullName: mshipObj.fullName,
                                        id: String(mshipObj.user?.id),
                                    },
                                });
                            } else if (result) {
                                // It's a LocalUser
                                const userObj = result as unknown as LocalUser;
                                setProperty({
                                    name: 'user',
                                    value: {
                                        fullName: userObj.fullName,
                                        id: String(userObj.id),
                                    },
                                });
                            }
                        }}
                        returnMship
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <SttSelectFacility
                        disabled={!!bookingSchedule.id}
                        name="facility"
                        caption={t('Facility')}
                        defVal={bookingSchedule.facility?.id ?? 0}
                        onChange={(facility) => {
                            setProperty({
                                name: 'facility',
                                value: facility as { id?: number } | undefined,
                            });
                        }}
                    />
                </Grid>
                {bookingSchedule.id && (
                    <Grid size={{ xs: 12 }}>
                        <SttBookingScheduleSlotList
                            slots={
                                slots as Array<{
                                    weekdays: number[];
                                    timeini: string;
                                    timeend: string;
                                }>
                            }
                            onAdd={() => {
                                setViewSlot({
                                    weekdays: [],
                                });
                            }}
                            onSelect={(slot) =>
                                setViewSlot(slot as BookingScheduleSlot)
                            }
                        />
                    </Grid>
                )}
            </Grid>
            <Box className="mt-6 flex justify-start gap-4 flex-wrap">
                {bookingSchedule.id && !bookingSchedule.trash && (
                    <SttButtonDelete
                        onClick={() => {
                            setOpenDeleteResponseDialog(true);
                        }}
                        permission={PERMISSIONS.FIXED_BOOKING.DELETE}
                    />
                )}

                {!bookingSchedule.trash && (
                    <SttButtonSave
                        loading={loading}
                        onClick={saveBookingSchedule}
                        permission={
                            bookingSchedule.id
                                ? PERMISSIONS.FIXED_BOOKING.MODIFY
                                : PERMISSIONS.FIXED_BOOKING.CREATE
                        }
                    />
                )}

                {!bookingSchedule.id && (
                    <SttButtonCancel onClick={() => navigate(-1)} />
                )}
            </Box>
            {viewSlot && (
                <BookingScheduleSlotDialog
                    item={viewSlot}
                    onSave={saveSlot}
                    onDelete={deleteSlot}
                    onClose={() => {
                        setViewSlot(null);
                    }}
                    loading={savingSlots}
                />
            )}
            <BookingScheduleDeleteDialog
                open={openDeleteResponseDialog}
                onClose={() => {
                    setOpenDeleteResponseDialog(false);
                }}
                onResponse={(userResponse: boolean) => {
                    deleteBookingSchedule(userResponse);
                }}
            />
            <SttDialog
                open={showCreatedInfo}
                onClose={() => setShowCreatedInfo(false)}
                title={t('additionalInfo')}
                buttons={[
                    {
                        type: 'accept',
                        caption: t('Understood'),
                        onClick: () => setShowCreatedInfo(false),
                    },
                ]}
                content={
                    <Box>
                        <p>{t('app.bookings.seeProgressInBookingTable')}</p>
                    </Box>
                }
            />
        </>
    );
}
